/* Slideshow flow — mixes images and videos into a Ken Burns timeline and
   records one video per aspect ratio. */
import { useEffect, useMemo, useRef, useState } from "react"
import { PhotoIcon, SparklesIcon } from "@heroicons/react/24/outline"
import { useDroppable, useDndMonitor, type DragEndEvent } from "@dnd-kit/core"
import { toast } from "react-toastify"
import {
  useAdForgeProxyImageMutation,
  useAdForgeProxyVideoMutation,
} from "~/ui-rtk/api/adForgeApi"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import type { ScrapedImage } from "../../types"
import { DRAWER_ITEM_PREFIX } from "../SelectedAssetsDrawer"
import KenBurnsPlayer, {
  type KenBurnsRef,
  type SlideMotion,
} from "../KenBurnsPlayer"
import SlideCard from "./SlideCard"
import { SLIDESHOW_DROP_ID } from "./constants"
import type { AiUploadOptions, Slide } from "./types"

interface MultiAssetFlowProps {
  /** The asset the ✨ button was clicked on, if any. Appended to the
   *  timeline (images AND videos both work as slides). */
  seedAsset: ScrapedImage | null
  /** Assets already staged in the ad card slots — the timeline opens
   *  pre-filled with them (in slot order) so an existing selection goes
   *  straight into the slideshow. */
  initialAssets?: ScrapedImage[]
  onGenerated: (
    file: File,
    uploadOptions?: AiUploadOptions,
  ) => Promise<void> | void
  onClose: () => void
}

export default function MultiAssetFlow({
  seedAsset,
  initialAssets,
  onGenerated,
  onClose,
}: MultiAssetFlowProps) {
  // Default slide config — applied when the user drops a new asset.
  const defaultDurationMs = 2500
  const defaultMotion: SlideMotion = "zoom-in"

  // Timeline state. Opens pre-filled with the staged slot assets (slot
  // order), plus the seed if it isn't one of them. The flow remounts on
  // every open, so this always reflects the current selection.
  const [slides, setSlides] = useState<Slide[]>(() => {
    const seedList = [...(initialAssets ?? [])]
    if (seedAsset && !seedList.some(a => a.id === seedAsset.id)) {
      seedList.push(seedAsset)
    }
    return seedList.map(a => ({
      asset: a,
      motion: defaultMotion,
      durationMs: defaultDurationMs,
    }))
  })

  // The panel stays mounted across source changes now, so clicking ✨ on
  // another card while the slideshow tab is open appends that asset to the
  // timeline instead of rebuilding it.
  useEffect(() => {
    if (!seedAsset) return
    setSlides(prev => {
      if (prev.some(s => s.asset.id === seedAsset.id)) return prev
      return [
        ...prev,
        {
          asset: seedAsset,
          motion: defaultMotion,
          durationMs: defaultDurationMs,
        },
      ]
    })
  }, [seedAsset?.id])

  const [isBusy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [proxyImage] = useAdForgeProxyImageMutation()
  const [proxyVideo] = useAdForgeProxyVideoMutation()
  const proxyImageAsync = async (url: string) => {
    const { base64 } = await proxyImage({ url }).unwrap()
    return base64
  }
  const proxyVideoAsync = async (videoUri: string) => {
    const { videoUrl } = await proxyVideo({ videoUri }).unwrap()
    return videoUrl
  }

  // One Ken Burns instance per supported aspect ratio. We generate all of
  // them in parallel so the user gets one asset per placement without
  // having to pick a ratio. (KenBurnsPlayer currently supports 1:1 and
  // 9:16; extending to 4:5 and 1.91:1 lands in a follow-up.)
  const kbRef1x1 = useRef<KenBurnsRef>(null)
  const kbRef9x16 = useRef<KenBurnsRef>(null)

  const droppable = useDroppable({
    id: SLIDESHOW_DROP_ID,
    data: { kind: "slideshow" },
  })

  // Watch drops globally so we can react when the user drags a drawer item
  // onto our empty slot. Both images and videos are valid slides — the
  // renderer plays video slides inline and Ken Burns-pans image slides.
  useDndMonitor({
    onDragEnd(event: DragEndEvent) {
      const { active, over } = event
      if (!over || String(over.id) !== SLIDESHOW_DROP_ID) return
      const id = String(active.id)
      if (!id.startsWith(DRAWER_ITEM_PREFIX)) return
      const data = active.data.current as
        | { kind: "pool"; asset: ScrapedImage }
        | undefined
      const asset = data?.asset
      if (!asset) return
      setSlides(prev => {
        if (prev.some(s => s.asset.id === asset.id)) return prev
        return [
          ...prev,
          {
            asset,
            motion: defaultMotion,
            durationMs: defaultDurationMs,
          },
        ]
      })
    },
  })

  const removeSlide = (assetId: string) =>
    setSlides(prev => prev.filter(s => s.asset.id !== assetId))
  const moveSlide = (assetId: string, direction: -1 | 1) =>
    setSlides(prev => {
      const idx = prev.findIndex(s => s.asset.id === assetId)
      const target = idx + direction
      if (idx < 0 || target < 0 || target >= prev.length) return prev
      const next = [...prev]
      const [moved] = next.splice(idx, 1)
      next.splice(target, 0, moved)
      return next
    })
  const updateSlide = (assetId: string, patch: Partial<Slide>) =>
    setSlides(prev =>
      prev.map(s => (s.asset.id === assetId ? { ...s, ...patch } : s)),
    )

  // Per-slide arrays plumbed straight into KenBurnsPlayer — drives both
  // the live preview and the recorded export so the motion + timing the
  // user picks are exactly what they ship with.
  const orderedAssets = useMemo(() => slides.map(s => s.asset), [slides])
  const slideMotions = useMemo(() => slides.map(s => s.motion), [slides])
  const slideDurations = useMemo(() => slides.map(s => s.durationMs), [slides])

  const handleGenerate = async () => {
    if (isBusy) return
    if (slides.length === 0) {
      toast.warning("Drag an asset onto the empty slot to start")
      return
    }
    setError(null)
    setBusy(true)
    try {
      // Render every supported aspect ratio in parallel so the result
      // covers as many placements as possible in one pass.
      const results = await Promise.all([
        kbRef1x1.current?.generateVideo(),
        kbRef9x16.current?.generateVideo(),
      ])
      const blobUrls = results.filter((u): u is string => !!u)
      if (blobUrls.length === 0) {
        throw new Error("Generation failed — no output")
      }

      // Upload each ratio as its own asset. Caller appends to the pool.
      // Parent linkage: the seed when set, else the first slide's asset.
      const parentAssetId = seedAsset?.assetId ?? slides[0]?.asset.assetId
      for (const [idx, blobUrl] of blobUrls.entries()) {
        const ratioLabel = idx === 0 ? "1x1" : "9x16"
        const blob = await fetch(blobUrl).then(r => r.blob())
        const extension = (blob.type || "").includes("webm") ? "webm" : "mp4"
        const file = new File(
          [blob],
          `ai-slideshow-${ratioLabel}-${Date.now()}.${extension}`,
          { type: blob.type || `video/${extension}` },
        )
        await onGenerated(file, {
          sourceType: "GEN_AI",
          parentAssetId,
          metadata: {
            generatedBy: "ai_slideshow",
            aspectRatio: idx === 0 ? "1:1" : "9:16",
          },
        })
      }
      toast.success(
        `${blobUrls.length} slideshow variant${blobUrls.length === 1 ? "" : "s"} added to your pool`,
      )
      setSlides([])
      onClose()
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Generation failed"
      setError(msg)
      toast.error(msg)
    } finally {
      setBusy(false)
    }
  }

  const dropHelpText =
    "Drag images or videos from the assets drawer onto the empty slot."

  return (
    <div className="space-y-4">
      {/* Preview — 9:16 instance is visible; 1:1 mounts hidden so the
          recorder can grab both ratios in parallel during Generate. */}
      <div className="flex justify-center min-h-[12rem]">
        {orderedAssets.length === 0 ? (
          <div className="w-40 aspect-[9/16] rounded-lg border-2 border-dashed border-system-gray-700/50 flex flex-col items-center justify-center text-center text-[11px] text-system-gray-500 px-3">
            <SparklesIcon className="w-5 h-5 opacity-50 mb-1" />
            Preview shows up here once a slide is added.
          </div>
        ) : (
          <>
            <KenBurnsPlayer
              ref={kbRef9x16}
              images={orderedAssets}
              aspectRatio="9:16"
              durations={slideDurations}
              motions={slideMotions}
              proxyImageUrl={proxyImageAsync}
              proxyVideoUrl={proxyVideoAsync}
            />
            <div className="hidden">
              <KenBurnsPlayer
                ref={kbRef1x1}
                images={orderedAssets}
                aspectRatio="1:1"
                durations={slideDurations}
                motions={slideMotions}
                proxyImageUrl={proxyImageAsync}
                proxyVideoUrl={proxyVideoAsync}
              />
            </div>
          </>
        )}
      </div>

      {/* Timeline */}
      <div>
        <div className="flex items-baseline justify-between mb-1.5">
          <span className="text-[10px] uppercase tracking-wide text-system-gray-500">
            Timeline ({slides.length})
          </span>
          <span className="text-[10px] text-system-gray-600">
            {dropHelpText}
          </span>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-2">
          {slides.map((slide, idx) => (
            <SlideCard
              key={slide.asset.id}
              index={idx}
              slide={slide}
              total={slides.length}
              disabled={isBusy}
              onRemove={() => removeSlide(slide.asset.id)}
              onMove={dir => moveSlide(slide.asset.id, dir)}
              onUpdate={patch => updateSlide(slide.asset.id, patch)}
            />
          ))}
          {/* Drop slot — always last. */}
          <div
            ref={droppable.setNodeRef}
            className={cn(
              "flex-shrink-0 w-24 self-stretch min-h-[6.5rem] rounded-lg border-2 border-dashed flex flex-col items-center justify-center text-center px-2 transition",
              droppable.isOver
                ? "border-brand bg-brand/10 text-brand"
                : "border-system-gray-700/50 text-system-gray-500",
            )}
          >
            <PhotoIcon className="w-5 h-5 opacity-50 mb-1" />
            <span className="text-[10px] font-semibold">
              {droppable.isOver ? "Drop to add" : "Drop here"}
            </span>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-md px-3 py-2 text-[11px] bg-system-error-500/10 text-system-error-500 border border-system-error-500/30">
          {error}
        </div>
      )}

      {/* Footer — the panel is always visible now, so "Cancel" resets the
          timeline (and the source selection) rather than closing anything. */}
      <div className="flex items-center justify-end gap-2 pt-2">
        <button
          onClick={() => {
            setSlides([])
            onClose()
          }}
          disabled={isBusy || slides.length === 0}
          className="btn btn-sm btn-ghost text-system-gray-400 disabled:opacity-50"
        >
          Clear
        </button>
        <button
          onClick={handleGenerate}
          disabled={isBusy || slides.length === 0}
          className="btn btn-sm bg-brand text-white hover:bg-brand/90 gap-1.5 disabled:opacity-50"
        >
          {isBusy ? (
            <span className="loading loading-spinner loading-xs" />
          ) : (
            <SparklesIcon className="w-3.5 h-3.5" />
          )}
          {isBusy ? "Generating all ratios…" : "Generate"}
        </button>
      </div>
    </div>
  )
}
